(() => {
  const script = document.getElementById('wbo-configuration-accessibility');
  if (!script) return;
  const strings = script.dataset;
  let nextId = 0;
  const invalidBefore = new WeakMap();
  const liveRegions = new WeakMap();
  const identified = (element) => {
    if (!element.id) element.id = `wbo-config-a11y-${++nextId}`;
    return element.id;
  };
  const appendReference = (element, attribute, id) => {
    const values = new Set((element.getAttribute(attribute) || '').split(/\s+/).filter(Boolean));
    values.add(id);
    element.setAttribute(attribute, [...values].join(' '));
  };
  const dropdowns = new WeakMap();
  const dropdownParts = (control) => {
    if (!control.matches('input.dropbtn.minimal[readonly]') || control.type !== 'text') return null;
    const holder = control.parentElement;
    const menu = control.nextElementSibling;
    if (!holder?.matches('div.option-type-dropdown') || !menu?.matches('div.option-type-dropdown-content')) return null;
    const options = [...menu.children];
    if (!options.length || !options.every((option) => option.matches('div.tab-view') && (
      option.querySelector('label.dropdown-label') || (option.id.endsWith('-DEFAULT') && option.querySelector(':scope > label'))
    ))) return null;
    if (menu.querySelector('input, select, textarea, button, a, [tabindex]')) return null;
    return { holder, menu, options };
  };
  const enhanceDropdown = (control) => {
    if (!dropdownParts(control)) return;
    let state = dropdowns.get(control);
    if (state) {
      state.sync();
      return;
    }
    state = { active: null, selected: null, open: false };
    const set = (element, attribute, value) => {
      if (element.getAttribute(attribute) !== value) element.setAttribute(attribute, value);
    };
    const isOpen = (menu) => !menu.hidden && getComputedStyle(menu).display !== 'none' && getComputedStyle(menu).visibility !== 'hidden';
    const sync = () => {
      const parts = dropdownParts(control);
      if (!parts) return null;
      const { menu, options } = parts;
      const open = isOpen(menu);
      const selected = options.find((option) => option.classList.contains('selected-dropdown-view')) || null;
      if (!open) state.active = null;
      else if (!state.open || !options.includes(state.active) || state.selected !== selected) state.active = selected || options[0];
      state.open = open;
      state.selected = selected;
      set(control, 'role', 'combobox');
      set(control, 'aria-haspopup', 'listbox');
      set(control, 'aria-autocomplete', 'none');
      set(control, 'aria-controls', identified(menu));
      set(control, 'aria-expanded', String(open));
      set(menu, 'role', 'listbox');
      const label = control.getAttribute('aria-labelledby');
      if (label) set(menu, 'aria-labelledby', label);
      options.forEach((option) => {
        set(option, 'role', 'option');
        set(option, 'aria-selected', String(option === selected));
        set(option, 'data-wbo-dropdown-active', String(open && option === state.active));
      });
      if (open && state.active) set(control, 'aria-activedescendant', identified(state.active));
      else control.removeAttribute('aria-activedescendant');
      return parts;
    };
    const close = () => {
      const parts = dropdownParts(control);
      if (parts && isOpen(parts.menu)) control.click();
      sync();
    };
    const commit = () => {
      const parts = dropdownParts(control);
      if (parts && isOpen(parts.menu) && parts.options.includes(state.active)) state.active.click();
      close();
    };
    state.sync = sync;
    dropdowns.set(control, state);
    control.addEventListener('keydown', (event) => {
      if (event.defaultPrevented || event.isComposing || event.ctrlKey || event.metaKey || control.disabled) return;
      const parts = sync();
      if (!parts) return;
      const { key } = event;
      if (key === 'Tab') {
        if (state.open) commit();
        return;
      }
      if (key === 'Escape') {
        if (state.open) {
          event.preventDefault();
          close();
        }
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(key)) return;
      if (event.altKey && key !== 'ArrowDown' && key !== 'ArrowUp') return;
      event.preventDefault();
      if ((key === 'Enter' || key === ' ') && event.repeat) return;
      if (state.open && (key === 'Enter' || key === ' ' || (event.altKey && key === 'ArrowUp'))) {
        commit();
        return;
      }
      if (!state.open) control.click();
      const opened = sync();
      if (!opened || !state.open) return;
      const { options } = opened;
      let index = options.indexOf(state.active);
      if (key === 'Home') index = 0;
      else if (key === 'End') index = options.length - 1;
      else if (!event.altKey && key === 'ArrowDown') index = Math.min(index + 1, options.length - 1);
      else if (!event.altKey && key === 'ArrowUp') index = Math.max(index - 1, 0);
      state.active = options[index];
      sync();
      state.active?.scrollIntoView?.({ block: 'nearest' });
    });
    dropdownParts(control).holder.addEventListener('click', () => queueMicrotask(sync));
    sync();
  };
  const enhance = (root) => {
    const messages = [];
    root.querySelectorAll('.optionHolder').forEach((holder) => {
      const heading = holder.querySelector('.option-label');
      if (!heading) return;
      const title = heading.textContent.trim();
      const required = /\*\s*$/.test(title);
      const controls = [...holder.querySelectorAll('input:not([type="button"]):not([type="hidden"]), select, textarea')];
      const radioGroup = holder.querySelector('.option-type-swatch, .option-type-radio, .option-type-button');
      if (radioGroup && radioGroup.querySelector('input[type="radio"]')) {
        radioGroup.setAttribute('role', 'radiogroup');
        radioGroup.setAttribute('aria-labelledby', identified(heading));
        if (required) radioGroup.setAttribute('aria-required', 'true');
      }
      controls.forEach((control) => {
        if (control.type === 'radio') {
          const visibleName = control.closest('.swatch-title-holder')?.querySelector('.swatch-title-text');
          if (visibleName?.textContent.trim()) control.setAttribute('aria-labelledby', identified(visibleName));
        } else {
          const dimension = control.closest('.inputHolder')?.querySelector('.option-label');
          if (dimension) {
            const suffix = control.tagName === 'SELECT' ? strings.fractionInches : strings.wholeInches;
            control.setAttribute('aria-label', `${dimension.textContent.trim()} (${suffix})`);
          } else if (!control.getAttribute('aria-label') && !control.getAttribute('aria-labelledby')) {
            control.setAttribute('aria-labelledby', identified(heading));
          }
          if (required) control.setAttribute('aria-required', 'true');
        }
        holder.querySelectorAll('.helpText').forEach((help) => {
          if (help.textContent.trim()) appendReference(control, 'aria-describedby', identified(help));
        });
        enhanceDropdown(control);
      });
      const invalid = new Set();
      holder.querySelectorAll('.errorMsgCss').forEach((error) => {
        const errorId = identified(error);
        const local = error.closest('.inputHolder');
        const targets = local ? controls.filter((control) => local.contains(control)) : controls;
        targets.forEach((control) => appendReference(control, 'aria-describedby', errorId));
        if (error.textContent.trim() && !error.hidden && getComputedStyle(error).display !== 'none') {
          messages.push(error.textContent.trim());
          targets.forEach((control) => invalid.add(control));
        }
      });
      controls.forEach((control) => {
        if (invalid.has(control)) {
          if (!invalidBefore.has(control)) invalidBefore.set(control, control.getAttribute('aria-invalid'));
          control.setAttribute('aria-invalid', 'true');
        } else if (invalidBefore.has(control)) {
          const previous = invalidBefore.get(control);
          if (previous === null) control.removeAttribute('aria-invalid');
          else control.setAttribute('aria-invalid', previous);
          invalidBefore.delete(control);
        }
      });
      holder.querySelectorAll('.minus1, .add1').forEach((button) => {
        button.setAttribute('aria-label', button.classList.contains('minus1') ? strings.decrease : strings.increase);
      });
    });
    let live = liveRegions.get(root);
    if (!live) {
      live = document.createElement('div');
      live.className = 'wbo-configuration-errors';
      live.setAttribute('role', 'alert');
      live.setAttribute('aria-atomic', 'true');
      root.appendChild(live);
      liveRegions.set(root, live);
    }
    const announcement = [...new Set(messages)].join('. ');
    if (live.textContent !== announcement) live.textContent = announcement;
  };
  const observed = new WeakSet();
  const discover = () => {
    document.querySelectorAll('product-options-section').forEach((root) => {
      if (observed.has(root)) return;
      observed.add(root);
      enhance(root);
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        queueMicrotask(() => {
          queued = false;
          enhance(root);
        });
      }).observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    });
  };
  discover();
  new MutationObserver(discover).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
})();
